import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // The endpoint is intentionally enumeration-safe — it should virtually
      // never error on bad emails. Only surface real failures (network/5xx).
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="block mb-12 text-center" data-testid="forgot-brand-link">
          <img
            src="/gcoms-logo.png"
            alt="GCOMS"
            className="w-16 h-16 mx-auto rounded-full bg-black p-1"
          />
          <div className="font-heading text-2xl font-bold tracking-tight mt-3">GCOMS.RN</div>
        </Link>

        <div className="module-card p-10 bg-white">
          {!sent ? (
            <>
              <div className="uppercase-label">Account recovery</div>
              <h1 className="font-heading text-3xl font-semibold mt-3">Forgot password?</h1>
              <p className="text-sm text-muted-foreground mt-3">
                Enter your account email and we&apos;ll send you a one-hour link to set a new password.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
                <button
                  data-testid="forgot-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-md bg-ink text-bone font-medium hover:bg-ink/90 disabled:opacity-60 transition-colors"
                >
                  {loading ? "Sending..." : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success-panel">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-sage/20 flex items-center justify-center shrink-0">
                  <CheckCircle2 size={22} className="text-sage-deep" />
                </div>
                <div>
                  <div className="uppercase-label">Check your inbox</div>
                  <h1 className="font-heading text-xl font-semibold mt-1">Reset link sent</h1>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-5 leading-relaxed">
                If <span className="font-mono text-ink">{email}</span> is on file, we just emailed
                a reset link. It expires in <b>1 hour</b> and can be used once.
              </p>
              <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
                <Mail size={13} className="mt-0.5 shrink-0" />
                <span>
                  Didn&apos;t get it? Check spam, or wait a minute and try again. Reset emails are sent
                  from <span className="font-mono">info@goldcoastoms.com</span>.
                </span>
              </div>
            </div>
          )}

          <div className="mt-8 text-sm text-muted-foreground text-center">
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="inline-flex items-center gap-1.5 text-ink hover:underline"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
